/**
 * Hero Split Screen Condensed - Ultra-Compact Premium Credit Card Hero
 * 
 * A highly condensed version that:
 * - Reduces vertical space by ~40% vs regular compact
 * - Uses 4-column inline metrics grid
 * - Shows 3 feature chips initially (vs 4)
 * - Abbreviated values (200k vs 200.000 €)
 * - Inline CTA + "Anzeige" label
 * - Perfect for maximizing content above fold
 */

import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { CardLogo } from './CardLogo';
import {
  Star, 
  Sparkles, 
  Plane, 
  Coins, 
  Ban, 
  TrendingDown, 
  CreditCard,
  Globe,
  Hotel,
  Car,
  ShoppingBag,
  Clock,
  Users,
  CheckCircle2,
  Phone,
  Wallet,
  Percent
} from 'lucide-react';

interface HeroCardData {
  name: string;
  bank: string;
  imageUrl?: string | null;
  logo?: string;
  subtitle?: string;
  effectiveRate: number;
  annualFee: number;
  creditLimit?: string | number;
  creditLimitMin?: number | null;
  creditLimitMax?: number | null;
  cardType?: string | null;
  interestFreePeriod: number;
  aprNominal?: number | null;
  
  // Feature flags
  travelInsurance?: boolean;
  feesForPurchasesAbroad?: number;
  hasCashback?: boolean;
  hasLounge?: boolean;
  hasCarRentalInsurance?: boolean;
  hasDiscounts?: boolean;
  isMembershipRequired?: boolean;
  hasContactless?: boolean;
  hasAccount?: boolean;
  isPrepaid?: boolean;
  hasHotline?: boolean;
  network?: string | null;
  currentAccountIncluded?: boolean;
}

interface HeroSplitScreenCondensedProps {
  card: HeroCardData;
  isPromoted?: boolean;
  onCTAClick?: () => void;
  onLearnMoreClick?: () => void;
  onTitleClick?: () => void;
  cardDetailUrl?: string;
  isLoading?: boolean;
  displayConfigChips?: Array<{ label: string; tooltip: string }>;
  isGerman?: boolean;
  /** Only the first card above the fold should load its image eagerly. */
  imagePriority?: boolean;
}

export function HeroSplitScreenCondensed({ 
  card, 
  isPromoted = false, 
  onCTAClick,
  onLearnMoreClick,
  onTitleClick,
  cardDetailUrl,
  isLoading = false,
  displayConfigChips = [],
  isGerman = false,
  imagePriority = false
}: HeroSplitScreenCondensedProps) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // SMART TAG GENERATION SYSTEM
  interface FeatureTag {
    icon: any;
    label: string;
    color: string;
    tooltip: string;
    priority: number;
  }

  // Use displayConfigChips if provided (from database), otherwise generate from card data
  const allFeatures: FeatureTag[] = displayConfigChips.length > 0 
    ? displayConfigChips.map((chip, index) => ({
        icon: getIconForChip(chip.label),
        label: chip.label,
        color: getColorForChip(chip.label),
        tooltip: chip.tooltip,
        priority: 10 - index,
      }))
    : generateFeaturesFromCard(card);

  const sortedFeatures = allFeatures.sort((a, b) => b.priority - a.priority);
  const visibleFeatures = showAllFeatures ? sortedFeatures : sortedFeatures.slice(0, 3);
  const hiddenCount = sortedFeatures.length - 3;

  const subtitle = card.subtitle || 'Kreditkarte mit Reiseversicherung und Cashback';

  // Card category: only real credit cards (revolving) may show interest metrics
  const category = (card.cardType?.toLowerCase() || (card.isPrepaid ? 'prepaid' : 'credit')) as
    | 'credit'
    | 'charge'
    | 'debit'
    | 'prepaid';
  const noCreditMetrics = category !== 'credit';
  const categoryLabel =
    category === 'prepaid'
      ? 'Prepaidkarte'
      : category === 'debit'
        ? 'Debitkarte'
        : category === 'charge'
          ? 'Charge-Karte'
          : 'Kreditkarte';
  const categoryTooltip =
    category === 'prepaid'
      ? 'Prepaidkarten haben keinen Kreditrahmen und damit keine Sollzinsen.'
      : category === 'debit'
        ? 'Debitkarten belasten das Girokonto direkt – kein Kreditrahmen, keine Sollzinsen.'
        : 'Charge-Karten werden monatlich vollständig abgebucht – keine Teilzahlung, keine Sollzinsen.';

  // One earned, data-backed highlight (never an editorial claim)
  const heroHighlight =
    card.annualFee === 0
      ? '0 € Jahresgebühr'
      : card.feesForPurchasesAbroad === 0
        ? 'Keine Fremdwährungsgebühr'
        : card.travelInsurance
          ? 'Reiseversicherung inklusive'
          : null;


  // Abbreviate credit limit for compact display
  const abbreviateCreditLimit = (limit?: string | number): string => {
    if (!limit) return '—';
    
    // If it's a number, convert to string with formatting
    if (typeof limit === 'number') {
      if (limit >= 1000) {
        return `${Math.round(limit / 1000)}k`;
      }
      return limit.toString();
    }
    
    // If it's a string, try to extract number
    const match = limit.match(/[\d.]+/);
    if (!match) return limit;
    const num = parseFloat(match[0].replace('.', ''));
    if (num >= 1000) {
      return `${Math.round(num / 1000)}k`;
    }
    return match[0];
  };

  // Get third slot content (matches HeroSplitScreen logic)
  const getThirdSlotContent = () => {
    // CASE 1: Not a revolving credit card
    if (noCreditMetrics) {
      return {
        label: 'Kartentyp',
        value: categoryLabel,
        tooltip: categoryTooltip
      };
    }
    
    // CASE 2: Valid Credit Limit Range
    if ((card.creditLimitMin && card.creditLimitMin > 0) || 
        (card.creditLimitMax && card.creditLimitMax > 0)) {
      const min = card.creditLimitMin || 0;
      const max = card.creditLimitMax || card.creditLimitMin || 0;
      
      let value: string;
      if (min > 0 && max > 0 && min !== max) {
        // Range: abbreviate both
        value = `${Math.round(min / 1000)}k–${Math.round(max / 1000)}k`;
      } else if (max > 0) {
        value = `${Math.round(max / 1000)}k`;
      } else {
        value = `${Math.round(min / 1000)}k`;
      }
      
      return {
        label: 'Kreditrahmen',
        value,
        tooltip: `Der Kreditrahmen zeigt, wie viel Sie maximal mit dieser Karte ausgeben können.`
      };
    }
    
    // CASE 3: Interest-Free Days Available
    if (card.interestFreePeriod > 0) {
      return {
        label: 'Zinsfreie Tage',
        value: `${card.interestFreePeriod}T`,
        tooltip: 'Innerhalb dieses Zeitraums fallen keine Zinsen auf Ihre Einkäufe an.'
      };
    }
    
    // CASE 4: Fallback - Monthly Cost Example
    const principal = 1500;
    const months = 12;
    const monthlyRate = card.effectiveRate / 100 / 12;
    const totalInterest = principal * monthlyRate * months;
    const totalCost = Math.round(principal + totalInterest);
    
    return {
      label: 'Beispielkst.',
      value: `${totalCost.toLocaleString('de-DE')} €`,
      tooltip: `Beispielrechnung: 1.500 € über 12 Monate – dient dem Vergleich der Kreditkartenkosten.`
    };
  };

  const thirdSlot = getThirdSlotContent();
  
  // Get fourth slot content (matches HeroSplitScreen logic)
  const getFourthSlotContent = () => {
    // CASE 1: Not a revolving credit card (no interest concept)
    if (noCreditMetrics) {
      return {
        label: 'Kartentyp',
        value: categoryLabel,
        tooltip: categoryTooltip
      };
    }

    // CASE 2: Has Interest-Free Days (show actual value)
    if (card.interestFreePeriod > 0) {
      return {
        label: 'Zinsfreie Tage',
        value: `${card.interestFreePeriod}T`,
        tooltip: 'Einkäufe bleiben zinsfrei bis zur Rechnungsstellung.'
      };
    }
    
    // CASE 3: Show Sollzins (Nominal APR) if available
    if (card.aprNominal && card.aprNominal > 0) {
      return {
        label: 'Sollzins',
        value: `${card.aprNominal.toFixed(2).replace('.', ',')}%`,
        tooltip: 'Effektiver Jahreszins bei Nutzung des Kreditrahmens.'
      };
    }
    
    // CASE 4: Show "0 € Gebühren" if annual fee is 0
    if (card.annualFee === 0) {
      return {
        label: 'Gebühren',
        value: '0 €',
        tooltip: 'Keine Jahres- oder Kontoführungsgebühren.'
      };
    }
    
    // CASE 5: Fallback - Generic "Kreditkarte" label
    return {
      label: 'Kartentyp',
      value: 'Kreditkarte',
      tooltip: 'Echte Kreditkarte mit flexibler Rückzahlung.'
    };
  };

  const fourthSlot = getFourthSlotContent();

  return (
    <div className="flex flex-col lg:flex-row min-h-[280px]">
      {/* LEFT PANEL: Card Showcase - Ultra Compact */}
      <div className="w-full lg:w-2/5 bg-gradient-to-br from-slate-100 to-gray-100 flex items-center justify-center p-6 lg:p-8 relative overflow-hidden group">
        {/* Subtle dot pattern background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(0 0 0 / 0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative w-full flex flex-col items-center">
          {/* Card Image - Smaller */}
          <div className="relative mb-4">
            {card.imageUrl ? (
              <div className="w-56 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200 transition-all duration-500 group-hover:scale-105 group-hover:shadow-3xl" style={{ aspectRatio: '3/2', height: '144px' }}>
                <CardLogo 
                  logoUrl={card.imageUrl}
                  bankName={card.bank}
                  className="block w-full h-full object-contain"
                  loading={imagePriority ? "eager" : "lazy"}
                  priority={imagePriority}
                  width={224}
                  height={144}
                />
              </div>
            ) : card.logo ? (
              <div className="w-56 h-36 rounded-2xl shadow-2xl bg-white flex items-center justify-center">
                <CardLogo 
                  logoUrl={card.logo} 
                  bankName={card.bank}
                  className="w-32 h-auto"
                />
              </div>
            ) : (
              <div className="w-56 h-36 rounded-2xl shadow-2xl bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500 text-sm">{card.name}</span>
              </div>
            )}
            {/* Subtle glow effect on hover */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400/0 to-emerald-400/0 group-hover:from-blue-400/10 group-hover:to-emerald-400/10 transition-all duration-500 pointer-events-none" />
          </div>
          
          {/* Feature Chips - Compact, show only 3 */}
          <TooltipProvider>
            <div className="flex flex-wrap gap-1.5 justify-center max-w-xs">
              {visibleFeatures.map((feature, index) => (
                <Tooltip key={index} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 rounded-full text-xs hover:border-gray-400 hover:shadow-xs transition-all cursor-help">
                      <feature.icon className={`w-3.5 h-3.5 ${feature.color}`} />
                      <span className="text-gray-700">{feature.label}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{feature.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
              
              {/* "+X" button - more compact */}
              {hiddenCount > 0 && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setShowAllFeatures(!showAllFeatures)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-300 rounded-full text-xs hover:bg-blue-100 hover:border-blue-400 hover:shadow-xs transition-all cursor-pointer"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-blue-700">
                        {showAllFeatures ? 'Weniger' : `+${hiddenCount}`}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{showAllFeatures ? 'Zusätzliche Vorteile ausblenden' : `Klicken Sie, um alle ${sortedFeatures.length} Vorteile zu sehen`}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </div>
      </div>
      
      {/* RIGHT PANEL: Card Information - Tighter Spacing */}
      <div className="w-full lg:w-3/5 bg-white flex items-center p-6 lg:p-8">
        <div className="max-w-md w-full mx-auto">
          {/* Factual badges: card type + at most one data-backed highlight */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="cursor-help">{categoryLabel}</Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm max-w-xs">{categoryTooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {heroHighlight && (
              <Badge className="bg-blue-50 text-blue-800 border-blue-200">{heroHighlight}</Badge>
            )}
          </div>

          
          {/* Card Title - Clickable if handler provided */}
          {onTitleClick || cardDetailUrl ? (
            <a
              href={cardDetailUrl}
              onClick={(e) => {
                if (onTitleClick) {
                  e.preventDefault();
                  onTitleClick();
                }
              }}
              className="block text-gray-900 text-xl lg:text-2xl mb-3 hover:text-primary transition-colors"
            >
              {card.name}
            </a>
          ) : (
            <h2 className="text-gray-900 text-xl lg:text-2xl mb-3">{card.name}</h2>
          )}
          
          {/* Compact Subtitle */}
          <p className="text-gray-600 text-sm mb-4 leading-snug">
            {subtitle}
          </p>
          
          {/* Metrics - Inline 4-column grid with prepaid logic matching HeroSplitScreen */}
          <TooltipProvider>
            <div className="grid grid-cols-4 gap-2 mb-5">
              {/* Box 1: APR only for real credit cards, otherwise Kartennetz */}
              {noCreditMetrics ? (
                <div className="border border-gray-200 rounded-lg p-2.5">
                  <div className="text-[11px] text-gray-600 mb-0.5">Kartennetz</div>
                  <div className="text-base text-gray-900">{card.network || 'Mastercard'}</div>
                </div>
              ) : (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="border border-gray-200 rounded-lg p-2.5 cursor-help hover:border-gray-300 transition-colors">
                      <div className="text-[11px] text-gray-600 mb-0.5">Eff. Zinssatz</div>
                      <div className="text-base text-gray-900">{`${card.effectiveRate}%`}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Effektiver Jahreszins bei Nutzung des Kreditrahmens.</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Box 2: Annual Fee */}
              <div className="border border-gray-200 rounded-lg p-2.5">
                <div className="text-[11px] text-gray-600 mb-0.5">Jahresgebühr</div>
                <div className="text-base text-gray-900">
                  {card.annualFee === 0 ? 'Gratis' : `${card.annualFee.toLocaleString('de-DE')} €`}
                </div>
              </div>

              {/* Box 3: Kartentyp for non-credit cards, otherwise limit/interest-free days */}
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <div className="border border-gray-200 rounded-lg p-2.5 cursor-help hover:border-gray-300 transition-colors">
                    <div className="text-[11px] text-gray-600 mb-0.5">{thirdSlot.label}</div>
                    <div className="text-base text-gray-900">{thirdSlot.value}</div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{thirdSlot.tooltip}</p>
                </TooltipContent>
              </Tooltip>

              {/* Box 4: Mit Girokonto for non-credit cards, otherwise other slot */}
              {noCreditMetrics ? (
                <div className="border border-gray-200 rounded-lg p-2.5">
                  <div className="text-[11px] text-gray-600 mb-0.5">Mit Girokonto</div>
                  <div className="text-base text-gray-900">{card.currentAccountIncluded ? 'Ja' : 'Nein'}</div>
                </div>
              ) : (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="border border-gray-200 rounded-lg p-2.5 cursor-help hover:border-gray-300 transition-colors">
                      <div className="text-[11px] text-gray-600 mb-0.5">{fourthSlot.label}</div>
                      <div className="text-base text-gray-900">{fourthSlot.value}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{fourthSlot.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
          
          {/* CTA Button with "Anzeige" below */}
          <div>
            <div className="flex items-center gap-3">
              {onCTAClick ? (
                <Button 
                  size="default"
                  onClick={onCTAClick}
                  disabled={isLoading}
                  className={isPromoted 
                    ? "bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all duration-300 group"
                    : ""
                  }
                >
                  <span className="group-hover:scale-105 inline-block transition-transform duration-300">
                    Jetzt beantragen
                  </span>
                </Button>
              ) : null}
              
              {onLearnMoreClick && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={onLearnMoreClick}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Mehr erfahren
                </Button>
              )}
            </div>
            
            {/* "Anzeige" indicator - below button */}
            {isPromoted && (
              <div className="text-xs text-gray-500 mt-2">Anzeige</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: Get icon for chip label
function getIconForChip(label: string): any {
  const iconMap: Record<string, any> = {
    'Gratis Jahresgebühr': Ban,
    'Kein Auslandseinsatzentgelt': Globe,
    'Reiseversicherung': Plane,
    'Teilzahlung': CreditCard,
    'Prepaid-Karte': Wallet,
    'Mit Girokonto': CheckCircle2,
    'Rabatte': ShoppingBag,
    'Kontaktlos': Sparkles,
    'Hotline': Phone,
    'Cashback': Coins,
    'Bonusprogramm': Star,
    'Lounge-Zugang': Hotel,
    'Mietwagen-Versicherung': Car,
    'Niedriger Zinssatz': TrendingDown,
    'Hoher Kreditrahmen': CreditCard,
    'Zinsfrei': Clock,
    'Für Mitglieder': Users,
  };
  
  return iconMap[label] || CheckCircle2;
}

// Helper: Get color for chip label
function getColorForChip(label: string): string {
  const colorMap: Record<string, string> = {
    'Gratis Jahresgebühr': 'text-emerald-600',
    'Kein Auslandseinsatzentgelt': 'text-cyan-600',
    'Reiseversicherung': 'text-blue-600',
    'Teilzahlung': 'text-purple-600',
    'Prepaid-Karte': 'text-orange-600',
    'Mit Girokonto': 'text-green-600',
    'Rabatte': 'text-pink-600',
    'Kontaktlos': 'text-indigo-600',
    'Hotline': 'text-slate-600',
    'Cashback': 'text-amber-600',
    'Bonusprogramm': 'text-yellow-600',
  };
  
  return colorMap[label] || 'text-gray-600';
}

// Helper: Generate features from card data
function generateFeaturesFromCard(card: HeroCardData): Array<{
  icon: any;
  label: string;
  color: string;
  tooltip: string;
  priority: number;
}> {
  const features = [];

  if (card.annualFee === 0) {
    features.push({
      icon: Ban,
      label: 'Gratis Jahresgebühr',
      color: 'text-emerald-600',
      tooltip: 'Keine Jahresgebühr – dauerhaft kostenlos',
      priority: 10,
    });
  }

  if (card.travelInsurance) {
    features.push({
      icon: Plane,
      label: 'Reiseversicherung',
      color: 'text-blue-600',
      tooltip: 'Inklusive Reiseversicherung für Sie und Ihre Familie',
      priority: 9,
    });
  }

  if (card.feesForPurchasesAbroad === 0) {
    features.push({
      icon: Globe,
      label: 'Kein Auslandseinsatzentgelt',
      color: 'text-cyan-600',
      tooltip: '0% Gebühren bei Auslandseinsatz',
      priority: 9,
    });
  }

  if (card.hasCashback) {
    features.push({
      icon: Coins,
      label: 'Cashback',
      color: 'text-amber-600',
      tooltip: 'Cashback-Programm bei allen Einkäufen',
      priority: 10,
    });
  }

  if (card.hasLounge) {
    features.push({
      icon: Hotel,
      label: 'Lounge-Zugang',
      color: 'text-purple-600',
      tooltip: 'Zugang zu über 1000 Airport Lounges weltweit',
      priority: 8,
    });
  }

  if (card.hasDiscounts) {
    features.push({
      icon: ShoppingBag,
      label: 'Rabatte',
      color: 'text-pink-600',
      tooltip: 'Exklusive Rabatte bei ausgewählten Partnern',
      priority: 6,
    });
  }

  if (card.hasContactless) {
    features.push({
      icon: Sparkles,
      label: 'Kontaktlos',
      color: 'text-indigo-600',
      tooltip: 'Kontaktloses Bezahlen mit NFC',
      priority: 5,
    });
  }

  if (card.hasHotline) {
    features.push({
      icon: Phone,
      label: 'Hotline',
      color: 'text-slate-600',
      tooltip: '24/7 Kundenservice-Hotline',
      priority: 5,
    });
  }

  return features;
}
